import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Snackbar,
    Chip,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ContentCopy as CopyIcon,
    OpenInNew as OpenInNewIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { internshipDomain } from '../../util/domain';

const ShortUrlManagement = () => {
    const navigate = useNavigate();
    const [shortUrls, setShortUrls] = useState([]);
    const [open, setOpen] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingUrl, setEditingUrl] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [copiedId, setCopiedId] = useState(null);

    const [formData, setFormData] = useState({
        long_url: '',
        title: '',
        description: '',
    });

    // Check user role and authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchShortUrls();
    }, [navigate]);

    const fetchShortUrls = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${internshipDomain}/api/shorturls`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Ensure response.data is always an array
            setShortUrls(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching short URLs:', error);
            setError('Failed to fetch short URLs');
            setShortUrls([]); // Set to empty array on error
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setEditingUrl(null);
        setFormData({ long_url: '', title: '', description: '' });
        setError('');
    };

    const handleClose = () => {
        setOpen(false);
        setEditingUrl(null);
        setFormData({ long_url: '', title: '', description: '' });
        setError('');
    };

    const handleOpenDelete = (url) => {
        setEditingUrl(url);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
        setEditingUrl(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.long_url) {
            setError('Long URL is required');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            if (editingUrl) {
                await axios.put(
                    `${internshipDomain}/api/shorturls/${editingUrl.hash_id}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showNotification('Short URL updated successfully', 'success');
            } else {
                await axios.post(
                    `${internshipDomain}/api/shorturls`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showNotification('Short URL created successfully', 'success');
            }
            fetchShortUrls();
            handleClose();
        } catch (error) {
            console.error('Error saving short URL:', error);
            setError('Failed to save short URL');
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${internshipDomain}/api/shorturls/${editingUrl.hash_id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showNotification('Short URL deleted successfully', 'success');
            fetchShortUrls();
            handleCloseDelete();
        } catch (error) {
            console.error('Error deleting short URL:', error);
            setError('Failed to delete short URL');
        }
    };

    const handleEdit = (url) => {
        setEditingUrl(url);
        setFormData({
            long_url: url.long_url,
            title: url.title || '',
            description: url.description || '',
        });
        setOpen(true);
    };

    const handleToggleActive = async (url) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${internshipDomain}/api/shorturls/${url.hash_id}`,
                { is_active: !url.is_active },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchShortUrls();
            showNotification(`Short URL ${!url.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
        } catch (error) {
            console.error('Error updating short URL:', error);
            setError('Failed to update short URL status');
        }
    };

    const handleCopy = async (code) => {
        const shortUrl = `https://vectorindia.org/s/${code}`;
        try {
            await navigator.clipboard.writeText(shortUrl);
            setCopiedId(code);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            alert("ShortURL : " + shortUrl);
            console.error('Failed to copy:', err);
        }
    };

    const showNotification = (message, severity) => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleOpenShortUrl = (code) => {
        window.open(`https://vectorindia.org/s/${code}`, '_blank');
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Short URL Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpen}
                    >
                        Create Short URL
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && !error && shortUrls && Array.isArray(shortUrls) && (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Short Code</TableCell>
                                    <TableCell>Long URL</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Clicks</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shortUrls.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                No short URLs found. Click "Create Short URL" to add one.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    shortUrls.map((url) => (
                                        <TableRow key={url.hash_id}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                        {url.short_code}
                                                    </Typography>
                                                    <Tooltip title={copiedId === url.short_code ? 'Copied!' : 'Copy URL'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(url.short_code)}
                                                        >
                                                            {copiedId === url.short_code ? <CheckCircleIcon color="success" /> : <CopyIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Open in new tab">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenShortUrl(url.short_code)}
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                    {`https://vectorindia.org/s/${url.short_code}`}
                                                </Typography>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                    {url.long_url}</Typography>
                                            </TableCell>
                                            <TableCell>{url.title || '-'}</TableCell>
                                            <TableCell>{url.click_count || 0}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={url.is_active ? 'Active' : 'Inactive'}
                                                    color={url.is_active ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(url.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleActive(url)}
                                                    color="primary"
                                                    title={url.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <Switch checked={url.is_active} size="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(url)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDelete(url)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
                }

                {/* Create/Edit Dialog */}
                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingUrl ? 'Edit Short URL' : 'Create Short URL'}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Long URL *"
                                value={formData.long_url}
                                onChange={(e) => setFormData({ ...formData, long_url: e.target.value })}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                margin="normal"
                            />
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                margin="normal"
                                multiline
                                rows={3}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained">
                            {editingUrl ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={openDelete} onClose={handleCloseDelete}>
                    <DialogTitle>Delete Short URL</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete this short URL? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDelete}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={handleCloseNotification}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseNotification} severity={notification.severity}>
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box >
        </Container >
    );
};

export default ShortUrlManagement;

